import React, { useState, useEffect, useContext } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTrash,
    faEye,
    faCheck,
    faTimes,
    faDownload,
    faSearch,
    faFilter,
    faUser,
    faCalendar,
    faShieldAlt,
    faExclamationTriangle,
    faInfoCircle,
    faSpinner,
    faChevronLeft,
    faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { UserContext } from '../../context/UserContext';
import { toast } from 'react-toastify';
import api from '../../api/api';
import { ACCOUNT_DELETION_STATUS } from '../../config/constants';

const AccountDeletions = () => {
    const [deletionRequests, setDeletionRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [adminNotes, setAdminNotes] = useState('');
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: 0,
        from: 0,
        to: 0
    });
    const [filters, setFilters] = useState({
        status: '',
        request_type: '',
        per_page: 20,
        page: 1
    });
    const [statistics, setStatistics] = useState({
        pending: 0,
        processing: 0,
        completed: 0,
        rejected: 0,
        total: 0,
        requests_this_month: 0,
        requests_last_30_days: 0,
        average_processing_time_hours: 0
    });

    useEffect(() => {
        fetchDeletionRequests();
        fetchStatistics();
    }, [filters]);

    const fetchDeletionRequests = async () => {
        try {
            setIsLoading(true);
            const params = new URLSearchParams();
            if (filters.status) params.append('status', filters.status);
            if (filters.request_type) params.append('request_type', filters.request_type);
            if (filters.per_page) params.append('per_page', filters.per_page);
            if (filters.page) params.append('page', filters.page);

            const response = await api.get(`/admin/account-deletion-requests?${params}`);

            if (response.data) {
                // Handle paginated response
                if (response.data.data) {
                    // Paginated response structure
                    setDeletionRequests(response.data.data || []);
                    setPagination({
                        current_page: response.data.current_page || 1,
                        last_page: response.data.last_page || 1,
                        per_page: response.data.per_page || 20,
                        total: response.data.total || 0,
                        from: response.data.from || 0,
                        to: response.data.to || 0
                    });
                } else {
                    // Non-paginated response (fallback)
                    const requests = Array.isArray(response.data) ? response.data : Object.values(response.data);
                    setDeletionRequests(requests || []);
                }

                // Calculate statistics from requests
                const requests = response.data.data || response.data;
                const stats = {
                    pending: 0,
                    processing: 0,
                    completed: 0,
                    rejected: 0,
                    total: Array.isArray(requests) ? requests.length : Object.values(requests).length
                };

                const requestArray = Array.isArray(requests) ? requests : Object.values(requests);
                requestArray.forEach(request => {
                    switch (request.status) {
                        case 'pending':
                            stats.pending++;
                            break;
                        case 'processing':
                            stats.processing++;
                            break;
                        case 'completed':
                            stats.completed++;
                            break;
                        case 'rejected':
                            stats.rejected++;
                            break;
                    }
                });

                setStatistics(stats);
            } else {
                toast.error('Failed to fetch deletion requests');
            }
        } catch (error) {
            console.error('Error fetching deletion requests:', error);
            if (error.response?.status === 401) {
                toast.error('Unauthorized access. Please login again.');
            } else if (error.response?.status === 403) {
                toast.error('Access denied. Admin privileges required.');
            } else {
                toast.error(error.response?.data?.message || 'Failed to fetch deletion requests');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const fetchStatistics = async () => {
        try {
            const response = await api.get('/admin/account-deletion-statistics');

            if (response.data) {
                // Map API response to our statistics format
                const apiStats = response.data;
                const mappedStats = {
                    pending: apiStats.pending_requests || 0,
                    processing: apiStats.processing_requests || 0,
                    completed: apiStats.completed_requests || 0,
                    rejected: apiStats.rejected_requests || 0,
                    total: apiStats.total_requests || 0,
                    requests_this_month: apiStats.requests_this_month || 0,
                    requests_last_30_days: apiStats.requests_last_30_days || 0,
                    average_processing_time_hours: apiStats.average_processing_time_hours || 0
                };
                setStatistics(mappedStats);
            } else {
                console.error('Failed to fetch statistics');
                // Calculate statistics from requests list as fallback
                calculateStatisticsFromRequests();
            }
        } catch (error) {
            console.error('Error fetching statistics:', error);
            if (error.response?.status === 401) {
                toast.error('Unauthorized access. Please login again.');
            } else if (error.response?.status === 403) {
                toast.error('Access denied. Admin privileges required.');
            } else {
                // Calculate statistics from requests list as fallback
                calculateStatisticsFromRequests();
            }
        }
    };

    const calculateStatisticsFromRequests = () => {
        const stats = {
            pending: 0,
            processing: 0,
            completed: 0,
            rejected: 0,
            total: deletionRequests.length
        };

        deletionRequests.forEach(request => {
            switch (request.status) {
                case 'pending':
                    stats.pending++;
                    break;
                case 'processing':
                    stats.processing++;
                    break;
                case 'completed':
                    stats.completed++;
                    break;
                case 'rejected':
                    stats.rejected++;
                    break;
            }
        });

        setStatistics(stats);
    };

    const handleProcessRequest = async (requestId, action) => {
        try {
            setIsLoading(true);

            const response = await api.post(`/admin/account-deletion-requests/${requestId}/process`, {
                action,
                admin_notes: adminNotes
            });

            if (response.data) {
                toast.success(`Request ${action}d successfully`);
                // Refresh the requests list
                await fetchDeletionRequests();
                await fetchStatistics();
                setShowModal(false);
                setAdminNotes('');
                setSelectedRequest(null);
            } else {
                toast.error(`Failed to ${action} request`);
            }
        } catch (error) {
            console.error('Error processing request:', error);
            if (error.response?.status === 401) {
                toast.error('Unauthorized access. Please login again.');
            } else if (error.response?.status === 403) {
                toast.error('Access denied. Admin privileges required.');
            } else if (error.response?.status === 404) {
                toast.error('Request not found.');
            } else if (error.response?.status === 422) {
                toast.error(error.response.data.message || 'Validation error');
            } else {
                toast.error(error.response?.data?.message || `Failed to ${action} request`);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleExportData = async (requestId) => {
        try {
            setIsLoading(true);

            const response = await api.get(`/admin/account-deletion-requests/${requestId}/export-data`, {
                responseType: 'blob',
                headers: {
                    'Accept': 'application/zip, application/octet-stream, */*'
                }
            });

            // Check if response is actually a blob
            if (!(response.data instanceof Blob)) {
                console.error('Response is not a blob:', response.data);

                // Check if it's a JSON error response
                if (typeof response.data === 'string') {
                    try {
                        const errorData = JSON.parse(response.data);
                        if (errorData.message) {
                            toast.error(errorData.message);
                            return;
                        }
                    } catch (e) {
                        // Not JSON, continue with original error
                    }
                }

                toast.error('Invalid file format received from server');
                return;
            }

            // Check if the blob has content
            if (response.data.size === 0) {
                toast.error('Empty file received from server');
                return;
            }

            // Check content type
            const contentType = response.headers['content-type'] || response.data.type;
            console.log('Content-Type:', contentType);
            console.log('Blob size:', response.data.size);

            // Check if response is JSON (error response) instead of ZIP
            if (contentType.includes('application/json')) {
                // Convert blob to text to read JSON error
                const text = await response.data.text();
                try {
                    const errorData = JSON.parse(text);
                    console.error('Server returned JSON error:', errorData);
                    toast.error(errorData.message || 'Server returned an error response');
                    return;
                } catch (e) {
                    console.error('Failed to parse JSON error:', text);
                    toast.error('Server returned invalid response format');
                    return;
                }
            }

            // Validate that it's actually a ZIP file
            if (!contentType.includes('zip') && !contentType.includes('octet-stream')) {
                console.error('Invalid content type:', contentType);
                toast.error('Server returned invalid file format. Expected ZIP file.');
                return;
            }

            // Create download link with proper MIME type
            const blob = new Blob([response.data], {
                type: 'application/zip'
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `user-data-${requestId}-${new Date().toISOString().split('T')[0]}.zip`);
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success('Data export downloaded successfully');
        } catch (error) {
            console.error('Error exporting data:', error);

            // Log detailed error information
            if (error.response) {
                console.error('Response status:', error.response.status);
                console.error('Response headers:', error.response.headers);
                console.error('Response data type:', typeof error.response.data);
                console.error('Response data size:', error.response.data?.size || 'N/A');

                // Check if response contains JSON error message
                if (error.response.data && typeof error.response.data === 'object') {
                    console.error('Response data:', error.response.data);
                    if (error.response.data.message) {
                        toast.error(error.response.data.message);
                        return;
                    }
                }
            }

            if (error.response?.status === 400) {
                toast.error('Request validation failed. Please check if the deletion request is verified.');
            } else if (error.response?.status === 401) {
                toast.error('Unauthorized access. Please login again.');
            } else if (error.response?.status === 403) {
                toast.error('Access denied. Admin privileges required.');
            } else if (error.response?.status === 404) {
                toast.error('Deletion request not found or export file not available.');
            } else if (error.response?.status === 409) {
                toast.error('Data export already in progress or completed.');
            } else if (error.response?.status === 500) {
                toast.error('Server error while generating zip file. Please try again.');
            } else {
                console.log('Error message:', error.message);
                toast.error(error.message || 'Failed to export data');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case ACCOUNT_DELETION_STATUS.PENDING:
                return <FontAwesomeIcon icon={faEye} className="text-yellow-500" />;
            case ACCOUNT_DELETION_STATUS.VERIFIED:
                return <FontAwesomeIcon icon={faCheck} className="text-blue-500" />;
            case ACCOUNT_DELETION_STATUS.PROCESSING:
                return <FontAwesomeIcon icon={faSpinner} className="text-blue-500 animate-spin" />;
            case ACCOUNT_DELETION_STATUS.COMPLETED:
                return <FontAwesomeIcon icon={faCheck} className="text-green-500" />;
            case ACCOUNT_DELETION_STATUS.REJECTED:
                return <FontAwesomeIcon icon={faTimes} className="text-red-500" />;
            default:
                return <FontAwesomeIcon icon={faEye} className="text-gray-500" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case ACCOUNT_DELETION_STATUS.PENDING:
                return 'bg-yellow-100 text-yellow-800';
            case ACCOUNT_DELETION_STATUS.VERIFIED:
                return 'bg-blue-100 text-blue-800';
            case ACCOUNT_DELETION_STATUS.PROCESSING:
                return 'bg-blue-100 text-blue-800';
            case ACCOUNT_DELETION_STATUS.COMPLETED:
                return 'bg-green-100 text-green-800';
            case ACCOUNT_DELETION_STATUS.REJECTED:
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    const handlePageChange = (page) => {
        setFilters({ ...filters, page });
        fetchDeletionRequests();
    };

    const renderSmartPagination = () => {
        const { current_page, last_page, total, from, to } = pagination;

        if (last_page <= 1) return null;

        const getVisiblePages = () => {
            const delta = 2; // Number of pages to show on each side of current page
            const range = [];
            const rangeWithDots = [];

            for (let i = Math.max(2, current_page - delta); i <= Math.min(last_page - 1, current_page + delta); i++) {
                range.push(i);
            }

            if (current_page - delta > 2) {
                rangeWithDots.push(1, '...');
            } else {
                rangeWithDots.push(1);
            }

            rangeWithDots.push(...range);

            if (current_page + delta < last_page - 1) {
                rangeWithDots.push('...', last_page);
            } else {
                rangeWithDots.push(last_page);
            }

            return rangeWithDots;
        };

        const visiblePages = getVisiblePages();

        return (
            <div className="flex items-center justify-between bg-white px-2 sm:px-4 py-3 sm:px-6">
                <div className="flex flex-1 justify-between sm:hidden">
                    <button
                        onClick={() => handlePageChange(current_page - 1)}
                        disabled={current_page <= 1}
                        className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Previous
                    </button>
                    <button
                        onClick={() => handlePageChange(current_page + 1)}
                        disabled={current_page >= last_page}
                        className="relative ml-2 sm:ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Next
                    </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                        <p className="text-xs sm:text-sm text-gray-700">
                            Showing <span className="font-medium">{from}</span> to <span className="font-medium">{to}</span> of{' '}
                            <span className="font-medium">{total}</span> results
                        </p>
                    </div>
                    <div>
                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                            <button
                                onClick={() => handlePageChange(current_page - 1)}
                                disabled={current_page <= 1}
                                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="sr-only">Previous</span>
                                <FontAwesomeIcon icon={faChevronLeft} className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>

                            {visiblePages.map((page, index) => (
                                <button
                                    key={index}
                                    onClick={() => typeof page === 'number' && handlePageChange(page)}
                                    disabled={page === '...'}
                                    className={`relative inline-flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold ${page === current_page
                                        ? 'z-10 bg-primary text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
                                        : page === '...'
                                            ? 'text-gray-500 cursor-default'
                                            : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                                        }`}
                                >
                                    {page}
                                </button>
                            ))}

                            <button
                                onClick={() => handlePageChange(current_page + 1)}
                                disabled={current_page >= last_page}
                                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="sr-only">Next</span>
                                <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>
                        </nav>
                    </div>
                </div>
            </div>
        );
    };

    const fetchRequestDetails = async (requestId) => {
        try {
            const response = await api.get(`/admin/account-deletion-requests/${requestId}`);

            if (response.data) {
                setSelectedRequest(response.data);
                setShowModal(true);
            } else {
                toast.error('Failed to fetch request details');
            }
        } catch (error) {
            console.error('Error fetching request details:', error);
            if (error.response?.status === 401) {
                toast.error('Unauthorized access. Please login again.');
            } else if (error.response?.status === 403) {
                toast.error('Access denied. Admin privileges required.');
            } else if (error.response?.status === 404) {
                toast.error('Request not found.');
            } else {
                toast.error(error.response?.data?.message || 'Failed to fetch request details');
            }
        }
    };

    return (
        <div className="min-h-screen">
            <div className="max-w-8xl mx-auto px-2 sm:px-4 lg:px-6 xl:px-8">
                {/* Header */}
                <div className="mb-6 sm:mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 flex items-center">
                                <FontAwesomeIcon icon={faTrash} className="mr-2 sm:mr-3 text-red-500 text-lg sm:text-xl" />
                                <span className="hidden sm:inline">Account Deletion Management</span>
                                <span className="sm:hidden">Account Deletions</span>
                            </h1>
                            <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">Manage user account deletion requests</p>
                        </div>
                        <button
                            onClick={() => {
                                fetchDeletionRequests();
                                fetchStatistics();
                            }}
                            disabled={isLoading}
                            className="px-3 sm:px-4 py-2 sm:py-2.5 bg-primary hover:bg-primary-dark text-white rounded-lg sm:rounded-xl font-semibold transition-colors duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base w-full sm:w-auto"
                        >
                            <FontAwesomeIcon icon={faSpinner} className={`${isLoading ? 'animate-spin' : ''} text-sm sm:text-base`} />
                            <span className="hidden sm:inline">Refresh</span>
                            <span className="sm:hidden">Refresh</span>
                        </button>
                    </div>
                </div>

                {/* Statistics Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
                    <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
                        <div className="flex items-center">
                            <div className="p-2 sm:p-3 bg-yellow-100 rounded-full">
                                <FontAwesomeIcon icon={faEye} className="text-yellow-600 text-sm sm:text-base" />
                            </div>
                            <div className="ml-3 sm:ml-4">
                                <p className="text-xs sm:text-sm font-medium text-gray-600">Pending</p>
                                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{statistics.pending || 0}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
                        <div className="flex items-center">
                            <div className="p-2 sm:p-3 bg-blue-100 rounded-full">
                                <FontAwesomeIcon icon={faSpinner} className="text-blue-600 text-sm sm:text-base" />
                            </div>
                            <div className="ml-3 sm:ml-4">
                                <p className="text-xs sm:text-sm font-medium text-gray-600">Processing</p>
                                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{statistics.processing || 0}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
                        <div className="flex items-center">
                            <div className="p-2 sm:p-3 bg-green-100 rounded-full">
                                <FontAwesomeIcon icon={faCheck} className="text-green-600 text-sm sm:text-base" />
                            </div>
                            <div className="ml-3 sm:ml-4">
                                <p className="text-xs sm:text-sm font-medium text-gray-600">Completed</p>
                                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{statistics.completed || 0}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
                        <div className="flex items-center">
                            <div className="p-2 sm:p-3 bg-red-100 rounded-full">
                                <FontAwesomeIcon icon={faTimes} className="text-red-600 text-sm sm:text-base" />
                            </div>
                            <div className="ml-3 sm:ml-4">
                                <p className="text-xs sm:text-sm font-medium text-gray-600">Rejected</p>
                                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{statistics.rejected || 0}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Additional Statistics Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
                    <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
                        <div className="flex items-center">
                            <div className="p-2 sm:p-3 bg-indigo-100 rounded-full">
                                <FontAwesomeIcon icon={faCalendar} className="text-indigo-600 text-sm sm:text-base" />
                            </div>
                            <div className="ml-3 sm:ml-4">
                                <p className="text-xs sm:text-sm font-medium text-gray-600">This Month</p>
                                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{statistics.requests_this_month || 0}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
                        <div className="flex items-center">
                            <div className="p-2 sm:p-3 bg-purple-100 rounded-full">
                                <FontAwesomeIcon icon={faShieldAlt} className="text-purple-600 text-sm sm:text-base" />
                            </div>
                            <div className="ml-3 sm:ml-4">
                                <p className="text-xs sm:text-sm font-medium text-gray-600">Last 30 Days</p>
                                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{statistics.requests_last_30_days || 0}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
                        <div className="flex items-center">
                            <div className="p-2 sm:p-3 bg-cyan-100 rounded-full">
                                <FontAwesomeIcon icon={faSpinner} className="text-cyan-600 text-sm sm:text-base" />
                            </div>
                            <div className="ml-3 sm:ml-4">
                                <p className="text-xs sm:text-sm font-medium text-gray-600">Avg Processing Time</p>
                                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{statistics.average_processing_time_hours || 0}h</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Status</label>
                            <select
                                value={filters.status}
                                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                className="w-full px-2 sm:px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
                            >
                                <option value="">All Statuses</option>
                                <option value="pending">Pending</option>
                                <option value="verified">Verified</option>
                                <option value="processing">Processing</option>
                                <option value="completed">Completed</option>
                                <option value="rejected">Rejected</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Request Type</label>
                            <select
                                value={filters.request_type}
                                onChange={(e) => setFilters({ ...filters, request_type: e.target.value })}
                                className="w-full px-2 sm:px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
                            >
                                <option value="">All Types</option>
                                <option value="account_deletion">Account Deletion</option>
                                <option value="data_export">Data Export</option>
                                <option value="both">Both</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Per Page</label>
                            <select
                                value={filters.per_page}
                                onChange={(e) => setFilters({ ...filters, per_page: parseInt(e.target.value) })}
                                className="w-full px-2 sm:px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
                            >
                                <option value={10}>10 per page</option>
                                <option value={20}>20 per page</option>
                                <option value={50}>50 per page</option>
                                <option value={100}>100 per page</option>
                            </select>
                        </div>

                        <div className="flex items-end sm:col-span-2 lg:col-span-1">
                            <button
                                onClick={fetchDeletionRequests}
                                className="w-full bg-primary hover:bg-primary-dark text-white py-2 sm:py-2.5 px-3 sm:px-4 rounded-lg sm:rounded-xl font-semibold transition-colors duration-200 flex items-center justify-center gap-2 text-sm sm:text-base"
                            >
                                <FontAwesomeIcon icon={faFilter} className="text-sm sm:text-base" />
                                <span className="hidden sm:inline">Apply Filters</span>
                                <span className="sm:hidden">Apply</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Requests List */}
                <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden">
                    <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900">Deletion Requests</h3>
                    </div>

                    {isLoading ? (
                        <div className="flex items-center justify-center py-8 sm:py-12">
                            <div className="w-6 h-6 sm:w-8 sm:h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    ) : deletionRequests.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 sm:py-12 px-4">
                            <FontAwesomeIcon icon={faTrash} className="text-4xl sm:text-6xl text-gray-300 mb-3 sm:mb-4" />
                            <h3 className="text-lg sm:text-xl font-semibold text-gray-500 mb-2 text-center">No Deletion Requests</h3>
                            <p className="text-gray-400 text-center text-sm sm:text-base max-w-md">
                                There are currently no account deletion requests. New requests will appear here when users submit them.
                            </p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-200">
                            {deletionRequests.map((request) => (
                                <motion.div
                                    key={request.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="p-4 sm:p-6 hover:bg-gray-50 transition-colors duration-200"
                                >
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                        <div className="flex items-center space-x-3 sm:space-x-4">
                                            <div className="p-2 sm:p-3 bg-gray-100 rounded-full">
                                                <FontAwesomeIcon icon={faUser} className="text-gray-600 text-sm sm:text-base" />
                                            </div>

                                            <div className="min-w-0 flex-1">
                                                <h4 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{request.user?.name}</h4>
                                                <p className="text-xs sm:text-sm text-gray-600 truncate">{request.user?.email}</p>
                                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                                    <FontAwesomeIcon icon={faCalendar} className="text-xs" />
                                                    {formatDate(request.requested_at)}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                                            <div className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getStatusColor(request.status)}`}>
                                                <div className="flex items-center gap-1 sm:gap-2">
                                                    {getStatusIcon(request.status)}
                                                    <span className="capitalize">{request.status}</span>
                                                </div>
                                            </div>

                                            <div className="flex space-x-1 sm:space-x-2">
                                                <button
                                                    onClick={() => fetchRequestDetails(request.id)}
                                                    className="p-1.5 sm:p-2 text-gray-600 hover:text-primary transition-colors duration-200"
                                                    title="View Details"
                                                >
                                                    <FontAwesomeIcon icon={faEye} className="text-sm sm:text-base" />
                                                </button>

                                                {request.status === 'pending' && request.can_process && (
                                                    <>
                                                        <button
                                                            onClick={() => handleProcessRequest(request.id, 'approve')}
                                                            disabled={isLoading}
                                                            className="p-1.5 sm:p-2 text-green-600 hover:text-green-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            title="Approve Request"
                                                        >
                                                            <FontAwesomeIcon icon={faCheck} className="text-sm sm:text-base" />
                                                        </button>

                                                        <button
                                                            onClick={() => handleProcessRequest(request.id, 'reject')}
                                                            disabled={isLoading}
                                                            className="p-1.5 sm:p-2 text-red-600 hover:text-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            title="Reject Request"
                                                        >
                                                            <FontAwesomeIcon icon={faTimes} className="text-sm sm:text-base" />
                                                        </button>
                                                    </>
                                                )}

                                                <button
                                                    onClick={() => handleExportData(request.id)}
                                                    disabled={isLoading}
                                                    className="p-1.5 sm:p-2 text-blue-600 hover:text-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title="Export Data"
                                                >
                                                    <FontAwesomeIcon icon={faDownload} className="text-sm sm:text-base" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {request.reason && (
                                        <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-gray-50 rounded-lg sm:rounded-xl">
                                            <p className="text-xs sm:text-sm text-gray-700">
                                                <strong>Reason:</strong> {request.reason}
                                            </p>
                                        </div>
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Request Details Modal */}
                {showModal && selectedRequest && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
                        <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 max-w-sm sm:max-w-2xl lg:max-w-4xl w-full mx-2 sm:mx-4 max-h-[90vh] overflow-y-auto">
                            <div className="flex items-center justify-between mb-4 sm:mb-6">
                                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 flex items-center">
                                    <FontAwesomeIcon icon={faEye} className="mr-2 sm:mr-3 text-blue-500 text-sm sm:text-base" />
                                    <span className="hidden sm:inline">Request Details</span>
                                    <span className="sm:hidden">Details</span>
                                </h3>
                                <button
                                    onClick={() => {
                                        setShowModal(false);
                                        setSelectedRequest(null);
                                        setAdminNotes('');
                                    }}
                                    className="text-gray-400 hover:text-gray-600 p-1 sm:p-2 hover:bg-gray-100 rounded-full"
                                >
                                    <FontAwesomeIcon icon={faTimes} className="text-sm sm:text-base" />
                                </button>
                            </div>

                            <div className="space-y-4 sm:space-y-6">
                                {/* User Information Section */}
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg sm:rounded-xl p-4 sm:p-6">
                                    <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                                        <FontAwesomeIcon icon={faUser} className="mr-2 text-blue-500 text-sm sm:text-base" />
                                        User Information
                                    </h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                        <div className="bg-white rounded-lg p-3 sm:p-4">
                                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Name</label>
                                            <p className="text-gray-900 font-semibold text-sm sm:text-base">{selectedRequest.user?.name || 'N/A'}</p>
                                        </div>
                                        <div className="bg-white rounded-lg p-3 sm:p-4">
                                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Email</label>
                                            <p className="text-gray-900 font-semibold text-sm sm:text-base break-all">{selectedRequest.user?.email || 'N/A'}</p>
                                        </div>
                                        <div className="bg-white rounded-lg p-3 sm:p-4">
                                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Phone</label>
                                            <p className="text-gray-900 font-semibold text-sm sm:text-base">{selectedRequest.user?.phone || 'N/A'}</p>
                                        </div>
                                        <div className="bg-white rounded-lg p-3 sm:p-4">
                                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Account Created</label>
                                            <p className="text-gray-900 font-semibold text-sm sm:text-base">{formatDate(selectedRequest.user?.created_at) || 'N/A'}</p>
                                        </div>
                                        <div className="bg-white rounded-lg p-3 sm:p-4">
                                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Email Verified</label>
                                            <p className="text-gray-900 font-semibold text-sm sm:text-base">{formatDate(selectedRequest.user?.email_verified_at) || 'N/A'}</p>
                                        </div>
                                        <div className="bg-white rounded-lg p-3 sm:p-4">
                                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Phone Verified</label>
                                            <p className="text-gray-900 font-semibold text-sm sm:text-base">{formatDate(selectedRequest.user?.phone_verified_at) || 'N/A'}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Request Information Section */}
                                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                        <FontAwesomeIcon icon={faTrash} className="mr-2 text-green-500" />
                                        Request Information
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="bg-white rounded-lg p-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Request ID</label>
                                            <p className="text-gray-900 font-semibold">#{selectedRequest.id}</p>
                                        </div>
                                        <div className="bg-white rounded-lg p-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedRequest.status)}`}>
                                                {getStatusIcon(selectedRequest.status)}
                                                <span className="ml-2 capitalize">{selectedRequest.status}</span>
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Request Type</label>
                                            <p className="text-gray-900 font-semibold capitalize">{selectedRequest.request_type?.replace('_', ' ')}</p>
                                        </div>
                                        <div className="bg-white rounded-lg p-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                                            <p className="text-gray-900 font-semibold">{selectedRequest.reason || 'No reason provided'}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Timeline Section */}
                                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                        <FontAwesomeIcon icon={faCalendar} className="mr-2 text-purple-500" />
                                        Timeline
                                    </h4>
                                    <div className="space-y-4">
                                        <div className="bg-white rounded-lg p-4 flex items-center">
                                            <div className="w-3 h-3 bg-yellow-500 rounded-full mr-4"></div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Requested At</label>
                                                <p className="text-gray-900 font-semibold">{formatDate(selectedRequest.requested_at)}</p>
                                            </div>
                                        </div>
                                        {selectedRequest.verified_at && (
                                            <div className="bg-white rounded-lg p-4 flex items-center">
                                                <div className="w-3 h-3 bg-blue-500 rounded-full mr-4"></div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Verified At</label>
                                                    <p className="text-gray-900 font-semibold">{formatDate(selectedRequest.verified_at)}</p>
                                                </div>
                                            </div>
                                        )}
                                        {selectedRequest.processed_at && (
                                            <div className="bg-white rounded-lg p-4 flex items-center">
                                                <div className="w-3 h-3 bg-green-500 rounded-full mr-4"></div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Processed At</label>
                                                    <p className="text-gray-900 font-semibold">{formatDate(selectedRequest.processed_at)}</p>
                                                </div>
                                            </div>
                                        )}
                                        {selectedRequest.processed_by && (
                                            <div className="bg-white rounded-lg p-4 flex items-center">
                                                <div className="w-3 h-3 bg-indigo-500 rounded-full mr-4"></div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Processed By</label>
                                                    <p className="text-gray-900 font-semibold">{selectedRequest.processed_by.name} ({selectedRequest.processed_by.email})</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Process Status Section */}
                                <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-6">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                        <FontAwesomeIcon icon={faShieldAlt} className="mr-2 text-orange-500" />
                                        Process Status
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="bg-white rounded-lg p-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Data Exported</label>
                                            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${selectedRequest.data_exported ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                <FontAwesomeIcon icon={selectedRequest.data_exported ? faCheck : faTimes} className="mr-1" />
                                                {selectedRequest.data_exported ? 'Yes' : 'No'}
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Account Deleted</label>
                                            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${selectedRequest.account_deleted ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                <FontAwesomeIcon icon={selectedRequest.account_deleted ? faCheck : faTimes} className="mr-1" />
                                                {selectedRequest.account_deleted ? 'Yes' : 'No'}
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Storage Cleared</label>
                                            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${selectedRequest.storage_cleared ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                <FontAwesomeIcon icon={selectedRequest.storage_cleared ? faCheck : faTimes} className="mr-1" />
                                                {selectedRequest.storage_cleared ? 'Yes' : 'No'}
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Is Verified</label>
                                            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${selectedRequest.is_verified ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                <FontAwesomeIcon icon={selectedRequest.is_verified ? faCheck : faTimes} className="mr-1" />
                                                {selectedRequest.is_verified ? 'Yes' : 'No'}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Data Summary Section */}
                                {selectedRequest.user?.data_summary && (
                                    <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-6">
                                        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                            <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-cyan-500" />
                                            Data Summary
                                        </h4>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                            <div className="bg-white rounded-lg p-4 text-center">
                                                <div className="text-2xl font-bold text-blue-600">{selectedRequest.user.data_summary.pets_count}</div>
                                                <div className="text-sm text-gray-600">Pets</div>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 text-center">
                                                <div className="text-2xl font-bold text-green-600">{selectedRequest.user.data_summary.deliveries_requested_count}</div>
                                                <div className="text-sm text-gray-600">Deliveries Requested</div>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 text-center">
                                                <div className="text-2xl font-bold text-purple-600">{selectedRequest.user.data_summary.deliveries_assigned_count}</div>
                                                <div className="text-sm text-gray-600">Deliveries Assigned</div>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 text-center">
                                                <div className="text-2xl font-bold text-orange-600">{selectedRequest.user.data_summary.walks_requested_count}</div>
                                                <div className="text-sm text-gray-600">Walks Requested</div>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 text-center">
                                                <div className="text-2xl font-bold text-pink-600">{selectedRequest.user.data_summary.walks_assigned_count}</div>
                                                <div className="text-sm text-gray-600">Walks Assigned</div>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 text-center">
                                                <div className="text-2xl font-bold text-indigo-600">{selectedRequest.user.data_summary.total_services}</div>
                                                <div className="text-sm text-gray-600">Total Services</div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Billing Impact Section */}
                                {selectedRequest.billing_impact && (
                                    <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-6">
                                        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2 text-yellow-500" />
                                            Billing Impact
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="bg-white rounded-lg p-4">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Active Subscription</label>
                                                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${selectedRequest.billing_impact.has_active_subscription ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                                                    <FontAwesomeIcon icon={selectedRequest.billing_impact.has_active_subscription ? faTimes : faCheck} className="mr-1" />
                                                    {selectedRequest.billing_impact.has_active_subscription ? 'Yes' : 'No'}
                                                </div>
                                            </div>
                                            <div className="bg-white rounded-lg p-4">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Cancellation Required</label>
                                                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${selectedRequest.billing_impact.subscription_cancellation_required ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                                                    <FontAwesomeIcon icon={selectedRequest.billing_impact.subscription_cancellation_required ? faTimes : faCheck} className="mr-1" />
                                                    {selectedRequest.billing_impact.subscription_cancellation_required ? 'Yes' : 'No'}
                                                </div>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 col-span-full">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Handling</label>
                                                <p className="text-gray-900 font-semibold">{selectedRequest.billing_impact.billing_handling}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Admin Notes Section */}
                                {selectedRequest.admin_notes && (
                                    <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl p-6">
                                        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                            <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-gray-500" />
                                            Admin Notes
                                        </h4>
                                        <div className="bg-white rounded-lg p-4">
                                            <p className="text-gray-900">{selectedRequest.admin_notes}</p>
                                        </div>
                                    </div>
                                )}

                                {/* Admin Action Section */}
                                {selectedRequest.status === 'pending' && selectedRequest.can_process && (
                                    <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-xl p-6">
                                        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                            <FontAwesomeIcon icon={faShieldAlt} className="mr-2 text-red-500" />
                                            Admin Actions
                                        </h4>
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Add Admin Notes</label>
                                                <textarea
                                                    value={adminNotes}
                                                    onChange={(e) => setAdminNotes(e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                                                    rows="3"
                                                    placeholder="Add notes about this request..."
                                                />
                                            </div>
                                            <div className="flex justify-end space-x-3">
                                                <button
                                                    onClick={() => handleProcessRequest(selectedRequest.id, 'reject')}
                                                    disabled={isLoading}
                                                    className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                                >
                                                    {isLoading && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
                                                    <FontAwesomeIcon icon={faTimes} />
                                                    Reject
                                                </button>
                                                <button
                                                    onClick={() => handleProcessRequest(selectedRequest.id, 'approve')}
                                                    disabled={isLoading}
                                                    className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                                >
                                                    {isLoading && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
                                                    <FontAwesomeIcon icon={faCheck} />
                                                    Approve
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Smart Pagination */}
                {renderSmartPagination()}
            </div>
        </div>
    );
};

export default AccountDeletions;
